import cn from 'clsx';
import { cloneElement, FC, isValidElement, useEffect, useState } from 'react';

interface Props {
  visible?: boolean;
  enterTime?: number;
  leaveTime?: number;
  clearTime?: number;
  className?: string;
  name?: string;
  onExited?: () => void;
  onEntered?: () => void;
  scss: Record<string, string>;
}

export type CSSTransitionProps = Props;

const CSSTransition: FC<React.PropsWithChildren<React.PropsWithChildren<CSSTransitionProps>>> = ({
  children,
  className = '',
  visible = false,
  enterTime = 60,
  leaveTime = 60,
  clearTime = 60,
  name = 'transition',
  onExited,
  onEntered,
  scss,
  ...props
}) => {
  const [classes, setClasses] = useState<string>('');
  const [renderable, setRenderable] = useState<boolean>(visible);

  useEffect(() => {
    const statusClassName = visible ? 'enter' : 'leave';
    const time = visible ? enterTime : leaveTime;
    if (visible && !renderable) {
      setRenderable(true);
    }

    setClasses(scss[`${name}-${statusClassName}`] as string);

    const timer = setTimeout(() => {
      setClasses(`${scss[`${name}-${statusClassName}`]}`);

      if (statusClassName === 'leave') {
        onExited?.();
      } else {
        onEntered?.();
      }
      clearTimeout(timer);
    }, time);

    const clearClassesTimer = setTimeout(() => {
      if (!visible) {
        setClasses('');
        setRenderable(false);
      }
      clearTimeout(clearClassesTimer);
    }, time + clearTime);

    return () => {
      clearTimeout(timer);
      clearTimeout(clearClassesTimer);
    };
  }, [visible, renderable]);

  if (!isValidElement(children) || !renderable) return null;
  return cloneElement(children, {
    ...props,
    className: cn((children.props as any).className, className, classes)
  } as React.PropsWithChildren<React.PropsWithChildren<unknown>>);
};
export default CSSTransition;
